// src/hooks/usePresence.js
import { useState, useEffect, useCallback } from 'react';
import presenceService from '../services/PresenceService';
import useAuth from './useAuth';

export const usePresence = () => {
    const { currentUser } = useAuth();
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [presenceLoading, setPresenceLoading] = useState(true);
    const [typingIndicators, setTypingIndicators] = useState({});

    // Subscribe to online users
    useEffect(() => {
        if (!currentUser) {
            setOnlineUsers([]);
            setPresenceLoading(false);
            return;
        }

        setPresenceLoading(true);

        // Initialize presence for current user
        presenceService.initialize(currentUser);

        const unsubscribe = presenceService.subscribeToOnlineUsers((users) => {
            setOnlineUsers(users || []);
            setPresenceLoading(false);
        });

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
            presenceService.cleanup();
        };
    }, [currentUser]);

    // Check if user is online
    const isUserOnline = useCallback((userId) => {
        if (!userId) return false;
        return onlineUsers.some(user => user.id === userId && user.online === true);
    }, [onlineUsers]);

    // Get user status color
    const getUserStatusColor = useCallback((userId) => {
        if (isUserOnline(userId)) {
            return '#10B981';
        }
        return '#9CA3AF';
    }, [isUserOnline]);

    // Subscribe to typing indicators for a channel
    const subscribeToTyping = useCallback((channelId, callback) => {
        if (!channelId) return null;

        const unsubscribe = presenceService.subscribeToTyping(channelId, (typingUsers) => {
            setTypingIndicators(prev => ({
                ...prev,
                [channelId]: typingUsers || []
            }));
            callback?.(typingUsers);
        });

        return unsubscribe;
    }, []);

    // Set typing status
    const setTyping = useCallback(async (channelId, isTyping) => {
        if (!currentUser || !channelId) return;
        await presenceService.setTyping(channelId, isTyping);
    }, [currentUser]);

    // Get typing users in a channel
    const getTypingUsers = useCallback((channelId) => {
        return typingIndicators[channelId] || [];
    }, [typingIndicators]);

    // Check if a specific user is typing in a channel
    const isUserTyping = useCallback((channelId, userId) => {
        const typingUsers = typingIndicators[channelId] || [];
        return typingUsers.some(u => u.uid === userId);
    }, [typingIndicators]);

    // Get typing text for a channel
    const getTypingText = useCallback((channelId) => {
        const typingUsers = typingIndicators[channelId] || [];
        const filtered = typingUsers.filter(u => u.uid !== currentUser?.uid);

        if (filtered.length === 0) return null;

        const names = filtered.map(u => u.displayName || 'User');

        if (names.length === 1) {
            return `${names[0]} is typing...`;
        } else if (names.length === 2) {
            return `${names[0]} and ${names[1]} are typing...`;
        } else {
            return `${names.length} people are typing...`;
        }
    }, [typingIndicators, currentUser]);

    return {
        onlineUsers,
        presenceLoading,
        typingIndicators,
        isUserOnline,
        getUserStatusColor,
        subscribeToTyping,
        setTyping,
        getTypingUsers,
        isUserTyping,
        getTypingText
    };
};

export default usePresence;